import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';
import API_URL from '../../config/api';

const AdminLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${API_URL}/admin/login`, {
                email,
                password
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                }
            });

            const data = response.data;

            // ✅ Save token securely
            localStorage.setItem("admin_token", data.token);
            localStorage.setItem("isAdminAuthenticated", "true");

            // Redirect to dashboard
            navigate('/admin/dashboard');

        } catch (err) {
            console.error("LOGIN ERROR:", err);
            
            if (err.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                setError(err.response.data.message || "Invalid email or password");
            } else if (err.request) {
                // The request was made but no response was received
                setError("Network error. Please check your connection.");
            } else {
                // Something happened in setting up the request that triggered an Error
                setError("An error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-light-bg relative overflow-hidden">

            {/* Background effects */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-brand-teal/10 blur-[120px] rounded-full" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-brand-teal/10 blur-[120px] rounded-full" />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md p-8 rounded-2xl bg-white border border-slate-200 shadow-2xl relative z-10"
            >

                {/* Header */}
                <div className="text-center mb-10">
                    <motion.div
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        className="w-16 h-16 bg-brand-teal rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-brand-teal/20"
                    >
                        <Lock className="text-white w-8 h-8" />
                    </motion.div>

                    <h1 className="text-3xl font-bold text-dark-text mb-2">
                        Admin Portal
                    </h1>
                    <p className="text-slate-500">
                        Codebytz Technologies
                    </p>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mb-4 p-3 bg-red-100 text-red-600 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleLogin} className="space-y-6">

                    {/* Email */}
                    <div>
                        <label className="text-sm font-medium text-slate-600">
                            Email
                        </label>

                        <div className="relative mt-2">
                            <Mail className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />

                            <input
                                type="email"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
                                placeholder="Enter email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div>
                        <label className="text-sm font-medium text-slate-600">
                            Password
                        </label>

                        <div className="relative mt-2">
                            <Lock className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />

                            <input
                                type="password"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
                                placeholder="Enter password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Button */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center py-4 bg-brand-teal text-white font-bold rounded-xl shadow-lg hover:bg-brand-light transition-all"
                    >
                        {loading ? "Signing in..." : "Sign In"}
                        <ArrowRight className="ml-2 w-5 h-5" />
                    </motion.button>
                </form>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t text-center text-sm text-slate-500">
                    © 2026 CODEBYTZ Technologies
                </div>
            </motion.div>
        </div>
    );
};

export default AdminLogin;